import * as React from 'react';
import {useHistory} from 'react-router-dom';
import {makeStyles} from '@material-ui/core';
import {useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {Row} from 'react-table';
import * as yup from 'yup';
import {toast} from 'react-hot-toast';
import DataGrid from 'components/DataGrid';
import {Typography, Grid, TextField, Select, MenuItem} from '@material-ui/core';
import {ERROR_MESSAGE, requiredFieldMessage} from 'utils/messages';
import {DATE_FORMAT} from 'utils/regex';
import {
  addDays,
  differenceInMilliseconds,
  isValid,
  subDays,
  differenceInSeconds,
  format,
} from 'date-fns';
import Button from 'components/Button';
import VisibilityIcon from '@material-ui/icons/Visibility';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import {startOfDayTimestamp, getDate} from 'utils/Date';
import {CarrierActivityResponse} from 'types';
import {carrierActivityService} from 'services/carrierActivity';

type FilterInputs = {
  status: 'active' | 'inactive';
  name: string;
  loggedInFrom: Date;
  loggedInTo: Date;
  city: string;
  activity: 'loggedIn';
};

const statusOptions = [
  {
    key: 'ACTIVE',
    value: 'active',
    text: 'Active',
  },
  {
    key: 'INACTIVE',
    value: 'inactive',
    text: 'Inactive',
  },
];

const activityOptions = [
  {
    key: 'loggedIn',
    value: 'loggedIn',
    text: 'Login',
  },
];

const filterSchema = yup.object().shape({
  status: yup.string().trim().required(requiredFieldMessage('Status')),
  name: yup.string(),
  city: yup.string(),
  loggedInFrom: yup
    .date()
    .typeError('Invalid date')
    .test('createdFromDateInvalid', 'Invalid date', function () {
      // validate Date
      const yupContext = this as any;
      if (typeof yupContext.originalValue === 'string') {
        return DATE_FORMAT.test(yupContext.originalValue);
      }
      return true;
    })
    .test(
      'createdToMin',
      'LoggedIn From should not be a future date',
      function () {
        // Min date validation
        if (isValid(this.parent.loggedInFrom)) {
          return (
            differenceInMilliseconds(new Date(), this.parent.loggedInFrom) >= 0
          );
        }
        return true;
      }
    ),

  loggedInTo: yup
    .date()
    .typeError('Invalid date ')
    .test('createdFromDateInvalid', 'Invalid date', function () {
      // validate Date
      const yupContext = this as any;
      if (typeof yupContext.originalValue === 'string') {
        return DATE_FORMAT.test(yupContext.originalValue);
      }
      return true;
    })
    .test('createdToMin', 'LoggedTo should not be a future date', function () {
      // Min date validation
      if (isValid(this.parent.loggedInTo)) {
        return (
          differenceInMilliseconds(new Date(), this.parent.loggedInTo) >= 0
        );
      }
      return true;
    })
    .test(
      'createdToMin',
      'LoggedOut cannot be before Logged From',
      function () {
        // Min date validation
        if (isValid(this.parent.loggedInFrom)) {
          return (
            differenceInMilliseconds(
              this.parent.loggedInTo,
              this.parent.loggedInFrom
            ) >= 0
          );
        }
        return true;
      }
    ),
});

const CarrierActivities: React.FC = () => {
  const history = useHistory();
  const classes = useStyles();

  const [data, setData] = React.useState<CarrierActivityResponse>({
    activities: [],
  });
  const [isFetching, setIsFetching] = React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
  } = useForm<FilterInputs>({
    mode: 'all',
    resolver: yupResolver(filterSchema),
    defaultValues: {
      status: 'active',
      name: '',
      loggedInFrom: subDays(new Date(startOfDayTimestamp(new Date())), 7),
      loggedInTo: new Date(startOfDayTimestamp(new Date())),
      city: '',
      activity: 'loggedIn',
    },
  });

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getActivities = async () => {
    try {
      setIsFetching(true);
      const response = await carrierActivityService.search({
        status: getValues('status'),
        name: getValues('name').trim() || '',
        loggedInFrom: startOfDayTimestamp(getValues('loggedInFrom')),
        loggedInTo: startOfDayTimestamp(
          addDays(new Date(getValues('loggedInTo')), 1)
        ),
        location: getValues('city').trim() || '',
      });
      setData(response);
    } finally {
      setIsFetching(false);
    }
  };

  const resetFilters = () => {
    reset({
      status: 'active',
      name: '',
      loggedInFrom: subDays(new Date(startOfDayTimestamp(new Date())), 7),
      loggedInTo: new Date(startOfDayTimestamp(new Date())),
      city: '',
    });
  };

  React.useEffect(() => {
    getActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearFilter = async (event: React.MouseEvent) => {
    resetFilters();
    getActivities();
  };

  const onSubmit = async (data: FilterInputs) => {
    getActivities();
  };
  return (
    <div>
      <Typography component={'h1'} className={classes.title}>
        Carrier Activities
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className={classes.filterContainer} spacing={2}>
          <Grid item xs={9}>
            <Typography className={classes.filterText} component={'h2'}>
              Filter
            </Typography>
          </Grid>
          <Grid
            container
            xs={3}
            spacing={2}
            alignItems="center"
            justify="flex-end"
          >
            <Grid item>
              <Button
                variant="outlined"
                color="inherit"
                fullWidth
                minWidth="0px"
                onClick={handleClearFilter}
                className={classes.cancelButton}
              >
                Clear
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                minWidth="0px"
                fullWidth
              >
                {'Apply'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid container className={classes.filterOptionContainer} spacing={2}>
          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>{'Status'}</Typography>
            <Controller
              name="status"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <>
                  <Select
                    value={value}
                    onChange={onChange}
                    displayEmpty
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    className={classes.select}
                    error={!!error}
                  >
                    {statusOptions.map(country => (
                      <MenuItem value={country.value} key={country.key}>
                        {country.text}
                      </MenuItem>
                    ))}
                  </Select>
                  {error?.message && (
                    <Typography
                      component={'p'}
                      className={classes.errorMessage}
                    >
                      {error.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Grid>

          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>First Name</Typography>
            <Controller
              name="name"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <>
                  <TextField
                    variant="outlined"
                    type="text"
                    placeholder="Add First Name"
                    fullWidth
                    margin="dense"
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                  />
                </>
              )}
            />
          </Grid>

          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>Activity From</Typography>
            <Controller
              name="loggedInFrom"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    inputFormat="dd-MMM-yyyy"
                    className={classes.datePicker}
                    disableFuture
                    allowSameDateSelection
                    value={value}
                    clearable
                    onChange={onChange}
                    maxDate={getDate(watch('loggedInTo'))}
                    renderInput={(params: any) => (
                      <TextField
                        variant="outlined"
                        type="text"
                        placeholder="Add Logged From"
                        fullWidth
                        margin="dense"
                        value={value}
                        onChange={onChange}
                        {...params}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>Activity To</Typography>
            <Controller
              name="loggedInTo"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    inputFormat="dd-MMM-yyyy"
                    className={classes.datePicker}
                    disableFuture
                    allowSameDateSelection
                    minDate={getDate(watch('loggedInFrom'))}
                    value={value}
                    clearable
                    onChange={onChange}
                    renderInput={(params: any) => (
                      <TextField
                        variant="outlined"
                        type="text"
                        placeholder="Add Logged Out"
                        fullWidth
                        margin="dense"
                        value={value}
                        onChange={onChange}
                        {...params}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>

          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>Location</Typography>
            <Controller
              name="city"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <>
                  <TextField
                    variant="outlined"
                    type="text"
                    placeholder="Add Location"
                    fullWidth
                    margin="dense"
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                  />
                </>
              )}
            />
          </Grid>

          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>{'Activity'}</Typography>
            <Controller
              name="activity"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <>
                  <Select
                    value={value}
                    onChange={onChange}
                    displayEmpty
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    className={classes.select}
                    error={!!error}
                  >
                    {activityOptions.map(country => (
                      <MenuItem value={country.value} key={country.key}>
                        {country.text}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
            />
          </Grid>
        </Grid>
      </form>
      <div className={classes.tableContainer}>
        <DataGrid
          columns={[
            {
              Header: 'Name',
              accessor: (row: any) =>
                `${row.user.firstName || ''} ${row.user.lastName || ''}`,
              maxWidth: 10,
            },
            {
              Header: 'Location',
              accessor: 'city',
              maxWidth: 10,
            },
            {
              Header: 'Activity',
              accessor: (row: any) => `Login`,
              maxWidth: 10,
            },
            {
              Header: 'Date (Time)',
              accessor: (row: any) =>
                `${format(new Date(row.logIn), 'dd-MMM-yyyy (HH:mm:ss)')}`,
              maxWidth: 10,
            },
            {
              Header: 'Duration',
              accessor: (row: any) =>
                `${Math.floor(
                  differenceInSeconds(
                    new Date(row.logOut),
                    new Date(row.logIn)
                  ) / 3600
                )
                  .toString()
                  .padStart(2, '0')}:${Math.floor(
                  (differenceInSeconds(
                    new Date(row.logOut),
                    new Date(row.logIn)
                  ) %
                    3600) /
                    60
                )
                  .toString()
                  .padStart(2, '0')}:${(
                  differenceInSeconds(
                    new Date(row.logOut),
                    new Date(row.logIn)
                  ) % 60
                )
                  .toString()
                  .padStart(2, '0')}`,
              maxWidth: 10,
            },
          ]}
          data={data.activities || []}
          recordCount={isFetching ? 0 : data.activities.length || 0}
          defaultPageSize={5}
          isLoading={isFetching}
          onPaginationChange={() => {}}
          extraActions={[
            {
              label: 'Action',
              type: 'icon-button',
              buttonContent: <VisibilityIcon />,
              handler: (row: Row<any>) => {
                if (row.original.id) {
                  history.push(`/caReport/${row.original.id}`);
                } else {
                  toast.error(ERROR_MESSAGE);
                }
              },
            },
          ]}
          // maxHeight="65vh"
          noDataMessage={'No items available'}
        />
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: 26,
    lineHeight: '40px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
      lineHeight: '28px',
    },
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(3),
  },
  errorMessage: {
    color: theme.palette.error.main,
    margin: '0 14px',
    fontSize: '0.75rem',
  },
  tabs: {
    marginBottom: '43px',
  },
  tab: {
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 14,
    lineHeight: '20px',
  },
  filterContainer: {
    width: '100%',
    background: '#fff',
    borderRadius: '5px 5px 0 0',
    padding: '12px 16px 15px',
    margin: 0,
    border: '1px solid',
    borderBottom: '0px',
    borderColor: theme.palette.grey[300],
  },
  filterOptionContainer: {
    width: '100%',
    background: '#fff',
    borderRadius: '0 0 5px 5px',
    padding: '12px 16px 15px',
    margin: 0,
    marginBottom: theme.spacing(3),
    border: '1px solid',
    borderColor: theme.palette.grey[300],
  },
  filterText: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '21px',
    lineHeight: '32px',
  },
  formElement: {},
  formLabel: {
    fontSize: 16,
    lineHeight: '28px',
  },
  hash: {
    backgroundColor: theme.palette.grey[400],
  },
  adornmentTextField: {
    width: '100%',
    '& span': {
      backgroundColor: theme.palette.grey[400],
    },
  },
  select: {
    marginTop: '8px',
    marginBottom: '4px',
  },
  datePicker: {
    backgroundColor: 'pink',
    '&& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: 'red !important',
      '&:hover': {
        backgroundColor: 'red !important',
      },
      '&:focus': {
        backgroundColor: 'red !important',
      },
    },
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  name: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
  },
  cancelButton: {
    color: theme.palette.grey[500],
  },
  content: {
    backgroundColor: '#fff',
    height: 'calc(100vh - 220px)',
    minHeight: '300px',
    padding: 30,
  },
}));

export default CarrierActivities;
